import {ApexOptions} from 'apexcharts';
import {defaultTo, find, fromPairs, get, isEmpty, map, round, sortBy, sum, values} from 'lodash';
import {v4 as uuid} from 'uuid';
import {SRStatus} from '../../../../../../global/api';
import {DashboardDto, StatusCount, Totals} from '../../../../../../shared/types/api-v2.types';
import {AppAny} from '../../../../../../shared/types/common.types';
import {STATUS_COLORS, STATUS_LABELS, STATUS_OPTIONS_MAP, STATUS_VALUES} from '../../constants/status';

const renderLegend = (seriesName: string, opts: AppAny) => {
  const seriesIndex = opts.seriesIndex;
  const values = get(opts, 'w.config.values', []);
  const total = sum(values);
  const value = get(values, seriesIndex, 0);
  const percent = round((value / total) * 100, 2);

  return `
    <span style='margin-left: 6px;'>
        ${seriesName}:
    </span>
    <span style='margin-left: auto;'>
        <span style='display: inline-block; width: 80px; text-align: center;'>
            <b>${value}</b>
        </span>
        <span style='display: inline-block; width: 60px; text-align: center; margin-left: 4px;'>
            <b>${percent}%</b>
        </span>
    </span>`;
};

const addTransparency = (hexColor: string, opacity: number = 0.2) => {
  const hex = hexColor.replace('#', '');
  const opacityHex = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, '0');
  return `#${hex}${opacityHex}`;
};

const MOVEMENT_STATUS_COLORS: Record<string, string> = {
  CREATED: '#A8ACB0',
  CANCELLED: '#A8ACB0',
  REJECTED: '#A8ACB0',
  READY_TO_PROCESS: '#3AC977',
  IN_PROGRESS: '#3AC977',
  EXECUTED: '#0D99FF',
  DONE: '#C64CFF',
  DELAYED: '#FF5E5E',
};

const getMovementStatusColor = (status?: string) => {
  if (!status) return STATUS_COLORS.NONE;
  return get(MOVEMENT_STATUS_COLORS, status, get(STATUS_COLORS, status, STATUS_COLORS.NONE));
};

const DONE_LIKE_STATUSES = new Set<string>([STATUS_VALUES.DONE, STATUS_VALUES.CANCELLED, STATUS_VALUES.REJECTED]);

const normalizeMovementStatus = (status?: string) => {
  if (!status) return status;
  return DONE_LIKE_STATUSES.has(status) ? STATUS_VALUES.DONE : status;
};

const aggregateStatusCounts = (totals?: Totals): StatusCount[] => {
  const filteredStatuses: Record<string, StatusCount> = {};

  totals?.statuses?.forEach((s) => {
    const {count} = s;
    const status = normalizeMovementStatus(s.status);
    if (status && count) {
      filteredStatuses[status] = {
        ...s,
        status,
        count: (filteredStatuses[status]?.count || 0) + count,
      };
    }
  });

  return values(filteredStatuses);
};

export const preparePrimaryChartData = (
  contracts: DashboardDto,
  srStatuses: SRStatus[] | null = [],
): {
  commonChart: {key: string; options: ApexOptions; series: number[]};
  partCharts: {key: string; options: ApexOptions; series: number[]}[];
} => {
  if (isEmpty(contracts)) return {commonChart: {key: '', options: {}, series: []}, partCharts: []};

  const aggregatedStatuses = aggregateStatusCounts(contracts.totals);
  const statuses = sortBy(aggregatedStatuses, ({status}) => get(find(srStatuses, {statusCode: status}), 'sortOrder'));
  const total = statuses.reduce((sum, {count}) => (count ? sum + count : sum), 0);
  const labels = fromPairs([
    ...map(srStatuses, (s) => [s.statusCode, s.statusName]),
    [STATUS_OPTIONS_MAP.ALL.value, STATUS_OPTIONS_MAP.ALL.label],
  ]);

  const CONTROL_VALUE = 100;
  const shouldAddOffset = total >= 10 * CONTROL_VALUE;

  const commonChart = {
    key: uuid(),
    options: {
      chart: {height: 267},
      plotOptions: {pie: {donut: {size: '68%'}}},
      dataLabels: {enabled: false},
      tooltip: {enabled: false},
      legend: {formatter: renderLegend, fontSize: '14px'},
      colors: statuses.map(({status = 'NONE'}) => getMovementStatusColor(status)),
      labels: statuses.map(({status = 'NONE'}) => get(labels, status, STATUS_LABELS.NONE)),
      values: statuses.map(({count}) => count || 0),
    },
    series: statuses.map(({count}) => {
      const value = defaultTo(count, 0);
      return shouldAddOffset && value < 10 ? value + 10 : value;
    }),
  };

  const partCharts = statuses.map(({status = 'NONE', count = 0}) => {
    const statusColor = getMovementStatusColor(status);
    return {
      key: uuid(),
      options: {
        chart: {height: 120},
        plotOptions: {
          pie: {
            donut: {
              size: '60%',
              labels: {
                show: true,
                value: {
                  show: true,
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#000',
                  offsetY: -2,
                  formatter: () => count?.toString(),
                },
                total: {
                  show: true,
                  label: '',
                  color: '#000',
                  formatter: () => count?.toString(),
                },
              },
            },
          },
        },
        dataLabels: {enabled: false},
        tooltip: {enabled: false},
        legend: {show: false},
        colors: [statusColor, addTransparency(statusColor)],
        labels: [STATUS_LABELS[status], 'Остальное'],
      } as ApexOptions,
      series: [count, total - count],
    };
  });

  return {commonChart, partCharts};
};
